import React from 'react';
import { Typography, Grid, Card, CardContent, Box } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import StorageIcon from '@mui/icons-material/Storage';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

const ChallengeSection = () => {
  return (
    <section id="challenge" className="whitepaper-section">
      <Typography variant="h2" className="section-title">
        The Data Security Challenge
      </Typography>
      
      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, rgba(96,165,250,0.15) 0%, rgba(59,130,246,0.1) 100%)',
            border: '2px solid rgba(96,165,250,0.3)',
            borderRadius: '16px',
            padding: '32px 24px',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: '0 12px 40px rgba(96,165,250,0.25)',
              borderColor: '#60a5fa'
            }
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <Box sx={{ 
                background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                borderRadius: '50%',
                width: 70,
                height: 70,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                boxShadow: '0 8px 24px rgba(96,165,250,0.4)'
              }}>
                <StorageIcon sx={{ fontSize: 36, color: 'white' }} />
              </Box>
              <Typography className="stat-value" sx={{ mb: 1 }}>10-100 TB</Typography>
              <Typography className="stat-label">Daily Security Data</Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.1) 100%)',
            border: '2px solid rgba(16,185,129,0.3)',
            borderRadius: '16px',
            padding: '32px 24px',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: '0 12px 40px rgba(16,185,129,0.25)',
              borderColor: '#10b981'
            }
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <Box sx={{ 
                background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                borderRadius: '50%',
                width: 70,
                height: 70,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                boxShadow: '0 8px 24px rgba(16,185,129,0.4)'
              }}>
                <TrendingUpIcon sx={{ fontSize: 36, color: 'white' }} />
              </Box>
              <Typography className="stat-value" sx={{ mb: 1 }}>40%</Typography>
              <Typography className="stat-label">Annual Growth Rate</Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, rgba(239,68,68,0.15) 0%, rgba(220,38,38,0.1) 100%)',
            border: '2px solid rgba(239,68,68,0.3)',
            borderRadius: '16px',
            padding: '32px 24px',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: '0 12px 40px rgba(239,68,68,0.25)',
              borderColor: '#ef4444'
            }
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <Box sx={{ 
                background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                borderRadius: '50%',
                width: 70,
                height: 70,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                boxShadow: '0 8px 24px rgba(239,68,68,0.4)'
              }}>
                <AttachMoneyIcon sx={{ fontSize: 36, color: 'white' }} />
              </Box>
              <Typography className="stat-value" sx={{ mb: 1 }}>$60M</Typography>
              <Typography className="stat-label">Enterprise SIEM Costs</Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="body1" sx={{ fontSize: '1.15rem', lineHeight: 1.8, mb: 3, color: '#e0e7ef' }}>
        Modern enterprises face unprecedented challenges in managing security data. Organizations now generate between 10TB and 100TB of security logs daily, with data volumes growing at 40% year-over-year. Traditional SIEM architectures struggle to keep pace with this explosive growth.
      </Typography>

      <Card className="highlight-card" sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ color: '#ef4444', fontWeight: 700 }}>
            Traditional Architecture Limitations
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <CancelIcon sx={{ color: '#ef4444' }} />
                <Box>
                  <Typography variant="h6" sx={{ color: '#e0e7ef', mb: 1 }}>Volume-Based Pricing</Typography>
                  <Typography sx={{ color: '#b0bec5' }}>$150-$250/GB for premium SIEMs results in $1M-$60M annual costs</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <CancelIcon sx={{ color: '#ef4444' }} />
                <Box>
                  <Typography variant="h6" sx={{ color: '#e0e7ef', mb: 1 }}>Performance Bottlenecks</Typography>
                  <Typography sx={{ color: '#b0bec5' }}>Search queries can take minutes to hours during peak loads</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <CancelIcon sx={{ color: '#ef4444' }} />
                <Box>
                  <Typography variant="h6" sx={{ color: '#e0e7ef', mb: 1 }}>Data Loss Risk</Typography>
                  <Typography sx={{ color: '#b0bec5' }}>Ingestion spikes overwhelm buffers, dropping critical security events</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <CancelIcon sx={{ color: '#ef4444' }} />
                <Box>
                  <Typography variant="h6" sx={{ color: '#e0e7ef', mb: 1 }}>Vendor Lock-in</Typography>
                  <Typography sx={{ color: '#b0bec5' }}>Proprietary formats prevent multi-vendor analytics and migration</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </section>
  );
};

export default ChallengeSection;
